import React from 'react';
import { Loader2, Heart, Activity } from 'lucide-react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'health' | 'pulse';
  message?: string;
  showProgress?: boolean;
  progress?: number;
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'default',
  message = 'Loading...',
  showProgress = false,
  progress = 0,
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  const renderSpinner = () => {
    switch (variant) {
      case 'health':
        return (
          <div className="relative">
            <Heart className={`${sizeClasses[size]} text-red-500 animate-pulse`} />
            <div className="absolute inset-0">
              <Heart className={`${sizeClasses[size]} text-red-300 animate-ping`} />
            </div>
          </div>
        );
      
      case 'pulse':
        return (
          <div className="relative">
            <Activity className={`${sizeClasses[size]} text-blue-500 animate-pulse`} />
            <div className="absolute inset-0">
              <Activity className={`${sizeClasses[size]} text-blue-300 animate-ping`} />
            </div>
          </div>
        );
      
      default:
        return (
          <Loader2 className={`${sizeClasses[size]} text-blue-500 animate-spin`} />
        );
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center space-y-4 ${className}`}>
      {renderSpinner()}
      
      {message && (
        <div className="text-center">
          <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">
            {message}
          </p>
        </div>
      )}
      
      {showProgress && (
        <div className="w-48">
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 text-center">
            {Math.round(progress)}%
          </p>
        </div>
      )}
    </div>
  );
};

// Full-screen loading overlay
interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  variant?: 'default' | 'health' | 'pulse';
  showProgress?: boolean;
  progress?: number;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isVisible,
  message = 'Loading ZippyHealth...',
  variant = 'default',
  showProgress = false,
  progress = 0
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-white dark:bg-slate-900 bg-opacity-90 dark:bg-opacity-90 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 p-8 max-w-sm w-full mx-4">
        <LoadingSpinner
          size="lg"
          variant={variant}
          message={message}
          showProgress={showProgress}
          progress={progress}
        />
      </div>
    </div>
  );
};

// Skeleton loading component
interface SkeletonProps {
  className?: string;
  lines?: number;
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className = '', 
  lines = 1 
}) => {
  return (
    <div className={`animate-pulse ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className="h-4 bg-slate-200 dark:bg-slate-700 rounded mb-2"
          style={{
            width: `${Math.random() * 40 + 60}%`
          }}
        />
      ))}
    </div>
  );
};

// Health-specific loading states
export const HealthLoadingStates = {
  // Loading health data
  LoadingHealthData: () => (
    <LoadingSpinner
      variant="health"
      message="Loading your health data..."
      size="lg"
    />
  ),
  
  // Loading analytics
  LoadingAnalytics: () => (
    <LoadingSpinner
      variant="pulse"
      message="Analyzing your health patterns..."
      size="lg"
    />
  ),
  
  // Processing data
  ProcessingData: ({ progress }: { progress?: number }) => (
    <LoadingSpinner
      variant="default"
      message="Processing your data..."
      showProgress={true}
      progress={progress}
      size="lg"
    />
  ),
  
  // Saving data
  SavingData: () => (
    <LoadingSpinner
      variant="default"
      message="Saving your data..."
      size="md"
    />
  ),
  
  // Syncing with devices
  SyncingDevices: () => (
    <LoadingSpinner
      variant="pulse"
      message="Syncing with your devices..."
      size="lg"
    />
  )
}; 